/**
 * Audio format conversion for bridged calls.
 *
 * The phone network side runs at 8 kHz, 16-bit mono (what Exotel streams); the
 * browser's audio runs at its own rate, usually 48 or 44.1 kHz, as floats.
 *
 * Both directions work from integer sample counts rather than a running
 * fractional position, so there is no floating-point drift: a minute of audio
 * stays exactly a minute, and nothing is lost or repeated at chunk joins.
 */

export const BRIDGE_SAMPLE_RATE = 8000;
/** 20 ms at 8 kHz — the frame size sent to the server. */
export const FRAME_SAMPLES = 160;

function toInt16(sample: number): number {
  const clamped = Math.max(-1, Math.min(1, sample));
  return clamped < 0 ? Math.round(clamped * 0x8000) : Math.round(clamped * 0x7fff);
}

/**
 * Microphone audio (floats at the browser's rate) -> 8 kHz PCM16 frames.
 * Output sample k averages input samples [k·r, (k+1)·r) with r = inRate/8000,
 * which also acts as a crude low-pass so speech stays clear without aliasing.
 */
export class Downsampler {
  /** Input not yet consumed; pending[0] is global input sample `base`. */
  private pending: number[] = [];
  private base = 0;
  /** Index of the next output sample. */
  private next = 0;
  private frame: number[] = [];

  constructor(private readonly inputRate: number) {
    if (!(inputRate >= BRIDGE_SAMPLE_RATE)) {
      throw new Error(`Unsupported input sample rate ${inputRate}`);
    }
  }

  /** Feed a chunk; returns every complete 20 ms frame now available. */
  push(input: Float32Array): Int16Array[] {
    for (let i = 0; i < input.length; i++) this.pending.push(input[i]);

    const frames: Int16Array[] = [];
    for (;;) {
      const start = Math.floor((this.next * this.inputRate) / BRIDGE_SAMPLE_RATE) - this.base;
      const end = Math.floor(((this.next + 1) * this.inputRate) / BRIDGE_SAMPLE_RATE) - this.base;
      if (end > this.pending.length) break;

      let sum = 0;
      for (let j = start; j < end; j++) sum += this.pending[j];
      this.frame.push(toInt16(sum / Math.max(1, end - start)));
      this.next += 1;

      if (this.frame.length === FRAME_SAMPLES) {
        frames.push(Int16Array.from(this.frame));
        this.frame = [];
      }
    }

    // Drop input every future output sample has passed.
    const keepFrom = Math.floor((this.next * this.inputRate) / BRIDGE_SAMPLE_RATE) - this.base;
    if (keepFrom > 0) {
      this.pending = this.pending.slice(keepFrom);
      this.base += keepFrom;
    }
    return frames;
  }
}

/**
 * 8 kHz PCM16 from the caller -> floats at the browser's playback rate, by
 * linear interpolation, one input sample behind so every output sample has
 * both neighbours. The previous frame's last sample is kept for the join.
 */
export class Upsampler {
  private previous = 0;
  /** Input samples received so far. */
  private received = 0;
  /** Output samples produced so far. */
  private produced = 0;

  constructor(private readonly outputRate: number) {
    if (!(outputRate >= BRIDGE_SAMPLE_RATE)) {
      throw new Error(`Unsupported output sample rate ${outputRate}`);
    }
  }

  push(frame: Int16Array): Float32Array {
    // samples[0] is the previous frame's last sample; samples[i + 1] is frame[i].
    const samples = new Float32Array(frame.length + 1);
    samples[0] = this.previous;
    for (let i = 0; i < frame.length; i++) samples[i + 1] = frame[i] / 0x8000;

    const before = this.received;
    this.received += frame.length;

    const out: number[] = [];
    // Output k sits at input time k·8000/outRate (one sample behind).
    while (this.produced * BRIDGE_SAMPLE_RATE < this.received * this.outputRate) {
      const local = (this.produced * BRIDGE_SAMPLE_RATE) / this.outputRate - before;
      const lo = Math.floor(local);
      const fraction = local - lo;
      out.push(samples[lo] + (samples[lo + 1] - samples[lo]) * fraction);
      this.produced += 1;
    }

    if (frame.length) this.previous = samples[frame.length];
    return Float32Array.from(out);
  }
}
